import type { PDFDocumentProxy } from 'pdfjs-dist';
import type { EventBus, PDFFindController, PDFViewer } from 'pdfjs-dist/web/pdf_viewer.mjs';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { PDFJS_ASSET_BASE } from '../../vite/pdfjsAssetBase';
import { loadPdfjs } from './pdfjsViewerModule';
import { type PdfSource, readPdfSource } from './pdfSource';

// Served by the pdfjsAssets Vite plugin. Without these, PDFs relying on the base-14
// fonts render with substitutes and JPEG2000/JBIG2 images fail to decode.
const ASSET_URLS = {
  standardFontDataUrl: `${PDFJS_ASSET_BASE}standard_fonts/`,
  cMapUrl: `${PDFJS_ASSET_BASE}cmaps/`,
  cMapPacked: true,
  wasmUrl: `${PDFJS_ASSET_BASE}wasm/`,
  iccUrl: `${PDFJS_ASSET_BASE}iccs/`,
} as const;

export type PdfStatus = 'loading' | 'ready' | 'error';

interface Args {
  containerRef: RefObject<HTMLDivElement | null>;
  src: PdfSource;
  httpHeaders?: Record<string, string>;
  withCredentials?: boolean;
  initialZoom: string | number;
  onError?: (error: Error) => void;
}

export const usePdfDocument = ({ containerRef, src, httpHeaders, withCredentials, initialZoom, onError }: Args) => {
  const [viewer, setViewer] = useState<PDFViewer | null>(null);
  const [eventBus, setEventBus] = useState<EventBus | null>(null);
  const [findController, setFindController] = useState<PDFFindController | null>(null);
  const [status, setStatus] = useState<PdfStatus>('loading');
  const [error, setError] = useState<Error | null>(null);

  // Held in a ref so a caller's inline error handler does not retrigger the load.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    setStatus('loading');
    setError(null);

    let cancelled = false;
    let teardown: (() => void) | null = null;

    const fail = (loadError: Error) => {
      if (cancelled) {
        return;
      }
      setError(loadError);
      setStatus('error');
      onErrorRef.current?.(loadError);
    };

    Promise.all([loadPdfjs(), readPdfSource(src)])
      .then(([{ lib, viewer }, source]) => {
        if (cancelled) {
          return;
        }
        const { EventBus, PDFFindController, PDFLinkService, PDFViewer } = viewer;

        const bus = new EventBus();
        const linkService = new PDFLinkService({ eventBus: bus });
        const finder = new PDFFindController({ eventBus: bus, linkService });
        const pdfViewer = new PDFViewer({
          container,
          eventBus: bus,
          linkService,
          findController: finder,
          // Form fields must stay visible. ENABLE draws only their appearance streams,
          // and in the lab templates those are empty — no border, no background, no value
          // — so the fields vanish. ENABLE_FORMS renders them as real inputs, which pdf.js
          // paints with a white background; that is what made them visible in the old
          // viewer. Typing remains possible and remains pointless: nothing is saved, and
          // there is no download or print.
          annotationMode: lib.AnnotationMode.ENABLE_FORMS,
        });
        linkService.setViewer(pdfViewer);

        const task = lib.getDocument({
          ...(typeof source === 'string' || source instanceof URL ? { url: source } : { data: source }),
          ...ASSET_URLS,
          httpHeaders,
          withCredentials,
        });

        const onPagesInit = () => {
          pdfViewer.currentScaleValue = String(initialZoom);
          // Assigning the scale makes pdf.js scroll the current page into view, which on
          // load leaves the container just below the first page's top margin. Only the
          // initial position needs correcting — later zoom changes should keep their spot.
          container.scrollTop = 0;
        };
        bus.on('pagesinit', onPagesInit);

        teardown = () => {
          bus.off('pagesinit', onPagesInit);
          // Destroying the loading task also tears down the resolved document.
          task.destroy();
          // pdf.js clears the viewer with a null document; its types don't model that.
          pdfViewer.setDocument(null as unknown as PDFDocumentProxy);
        };

        task.promise.then(loaded => {
          if (cancelled) {
            return;
          }
          pdfViewer.setDocument(loaded);
          linkService.setDocument(loaded, null);
          setViewer(pdfViewer);
          setEventBus(bus);
          setFindController(finder);
          setStatus('ready');
        }, fail);
      })
      .catch(fail);

    return () => {
      cancelled = true;
      teardown?.();
      setViewer(null);
      setEventBus(null);
      setFindController(null);
    };
  }, [containerRef, src, httpHeaders, withCredentials, initialZoom]);

  return { viewer, eventBus, findController, status, error };
};
